import { Authentication } from "./auth";
import { PasswordChangeRequest, User } from "../types";
import { oktaForgotPassword } from "@/shared/api/userService";
import { UserClaims } from "@okta/okta-auth-js/lib/types";
import { loginUrl } from "@/shared/custodianSettings/employee/hsaBank";
import { OktaAuth as OktaAuthJS, OktaAuthOptions, SessionObject, TokenResponse } from "@okta/okta-auth-js";

export class OktaAuth implements Authentication {
  private oktaAuthApi: OktaAuthJS;

  constructor (config: OktaAuthOptions) {
    this.oktaAuthApi = new OktaAuthJS(config);
  }

  promiseOfUserClaims = () => {
    return new Promise<UserClaims> ((resolve, reject) => {
      return this.oktaAuthApi.getUser()
      .then(success => resolve(success))
      .catch(error => reject(error));
     });
   };

   promiseOfValidateGroups = (requiredGroups: ReadonlyArray<string>, user: User) => {
     return new Promise<User> ((resolve, reject) => {
       if (requiredGroups.length === 0) {
         resolve(user);
         return;
       }
       if (user.groups.some(userGroup => requiredGroups.includes(userGroup))) {
         resolve(user);
         return;
       }

       return reject({ loginUrl, missingRequiredGroup: true });
     });
   };

  mapUserAttributes (tokens: any, userClaims: UserClaims): User {
    const user: User = {
      displayName: userClaims.name,
      groups: tokens.accessToken.claims.groups,
      firstName: userClaims.given_name,
      lastName: userClaims.family_name,
      email: userClaims.email,
      username: userClaims.preferred_username
    };

    return user;
  }

  postLoginProcess (requiredGroups: ReadonlyArray<string>): Promise<User> {
    return this.oktaAuthApi.tokenManager.getTokens()
      .then(tokens =>
        this.promiseOfUserClaims().then(userClaims => ({ tokens, userClaims})))
      .then(({tokens, userClaims}) =>
        this.mapUserAttributes(tokens, userClaims))
      .then((user: User) =>
          this.promiseOfValidateGroups(requiredGroups, user));
  }

  getUser (): null | { username: string } {
    let user = null;
    this.promiseOfUserClaims()
      .then(claims => {
        user = claims.preferred_username;
      })
      .catch(() => user = null);

    return user ? { username: user } : null;
  }

  setInitialPassword: (username: string, oldPassword: string, newPassword: string) => Promise<User>;

  resetPassword (username: string): Promise<any> {
    return oktaForgotPassword(username);
  }

  changePassword: (request: PasswordChangeRequest) => Promise<void>;
  autoRecoverUser: (email: string) => Promise<any>;
  confirmPassword: (username: string, verificationCode: string, password: string) => Promise<any>;

  login (username: string, password: string, requiredGroups: ReadonlyArray<string>, mfa): Promise<User> {
    return this.postLoginProcess(requiredGroups);
  }

  logout (): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.oktaAuthApi.isAuthenticated()
      .then(async isAuthenticated => {
        if (isAuthenticated) {
          await this.oktaAuthApi.revokeAccessToken();
          this.oktaAuthApi.closeSession()
          .then(() => {
            resolve();
          }).catch(error => {
            reject(error);
            return;
          });
        }
      });
    });
  }

  accessToken (): Promise<string> {
    return this.oktaAuthApi.isAuthenticated({ onExpiredToken: "renew"})
    .then(isAuthenticated => {
      if (isAuthenticated) {
       return this.oktaAuthApi.tokenManager.getTokens()
        .then( ({ accessToken }) => {
          return accessToken.accessToken;
        });
      } else {
        return "";
      }
    });
  }

  forceRefreshSession (): Promise<User> {
    return this.oktaAuthApi.tokenManager.renew("accessToken")
    .then( _ => {
      return this.oktaAuthApi.tokenManager.getTokens()
      .then(tokens =>
         this.promiseOfUserClaims().then(userClaims => ({ tokens, userClaims})))
      .then(({tokens, userClaims}) =>
          this.mapUserAttributes(tokens, userClaims));
    });
  }

  getSession (): Promise<SessionObject> {
    return this.oktaAuthApi.session.get();
  }

  setTokens (tokens) {
    this.oktaAuthApi.tokenManager.setTokens(tokens);
  }

  getWithoutPrompt (): Promise<TokenResponse> {
    return this.oktaAuthApi.token.getWithoutPrompt();
  }
}
